import dayjs from "dayjs";

export const tablePageSizeOptions = [25, 50, 75, 100];

export const searchInitiateDelayTime = 1 * 1000;

export const downloadFile = async (fileUrl, fileName = "download.pdf") => {
    if (!fileUrl) return;
    try {
        const response = await fetch(fileUrl);
        if (!response.ok) throw new Error("Failed to fetch file");

        const fileExtension = fileName.split(".").pop().toLowerCase();
        const mimeTypes = {
            pdf: "application/pdf",
            doc: "application/msword",
            docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            xls: "application/vnd.ms-excel",
            xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            jpg: "image/jpeg",
            jpeg: "image/jpeg",
            png: "image/png",
            gif: "image/gif",
            zip: "application/zip"
        };

        const mimeType = mimeTypes[fileExtension] || response.headers.get("content-type") || "application/octet-stream";
        const blob = await response.blob();
        const correctedBlob = new Blob([blob], { type: mimeType });

        const link = document.createElement("a");
        link.href = URL.createObjectURL(correctedBlob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(link.href);
    } catch (error) {
        console.error("Error downloading file:", error);
    }
};

export const formatPhoneNumber = (number) => {
    const digits = number?.toString()?.replace(/\D/g, "");
    if (digits?.startsWith("91")) {
        return `+91 ${digits?.slice(2)}`;
    }
    return `+91 ${digits}`;
};

export const formatAmount = (amount) => {
    const num = Number(amount) || 0;
    if (Number.isInteger(num)) {
        return num.toString();
    }
    return num.toFixed(2);
};

export const isConsultationRefundExpired = (consultationDate) => {
    if (!consultationDate) return false;
    const consDate = dayjs(consultationDate);
    if (!consDate.isValid()) return false;
    return dayjs().startOf("day").diff(consDate.startOf("day"), "day") > 2;
};
export const formatUpToOneCrore = (value) => {
    const numericValue = Number(value);
    if (!numericValue) return "0";
    const ONE_CRORE = 10000000;
    if (numericValue > ONE_CRORE) {
        return `${ONE_CRORE - 1}+`;
    }
    return numericValue.toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    });
};

export const playMediaByRef = (mediaElementRef) => {
    if (mediaElementRef?.current?.play) {
        mediaElementRef?.current?.play();
    }
};

export const downloadFileByUrl = (fileUrl, fallbackFileName = "consultation-recording.mp4") => {
    if (!fileUrl) {
        return;
    }
    const downloadLink = document.createElement("a");
    downloadLink.href = fileUrl;
    downloadLink.target = "_blank";
    downloadLink.rel = "noopener noreferrer";
    downloadLink.download = fileUrl?.split("/")?.pop() || fallbackFileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
};

export const rupeeSymbol = <>&#8377;</>

export { markdownToHtml } from "./markdownToHtml";

export const openEmailComposer = ({ toEmail = "", ccEmail = "", subject = "", body = "" }) => {
    if (!toEmail && !ccEmail) return;

    const to = toEmail ? toEmail.split(/[,;]/).map(e => e.trim()).filter(Boolean).join(",") : "";
    const cc = ccEmail ? ccEmail.split(/[,;]/).map(e => e.trim()).filter(Boolean).join(",") : "";

    const params = [];
    if (cc) params.push(`cc=${encodeURIComponent(cc)}`);
    if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
    if (body) params.push(`body=${encodeURIComponent(body)}`);

    const queryString = params.length > 0 ? `?${params.join("&")}` : "";
    const mailtoUrl = `mailto:${to}${queryString}`;

    setTimeout(() => {
        try {
            const link = document.createElement("a");
            link.href = mailtoUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error("Failed to open mail client:", err);
            window.location.href = mailtoUrl;
        }
    }, 400);
};

export const exportAdvocateReportToExcel = (data = [], fileName = "advocate_report", activeTab = "APPROVED", emailDetails = null) => {
    if (!data || data.length === 0) return;

    const headers = [
        "S.No",
        "Submerchant ID",
        "Submerchant Name",
        "Bar Council ID",
        "Account Number",
        "IFSC Code",
        "Split Label"
    ];

    const escapeCsvValue = (val) => {
        if (val === null || val === undefined) return '""';
        const str = String(val).replace(/"/g, '""');
        return `"${str}"`;
    };

    const rows = data.map((item, index) => {
        const first = item?.firstName || item?.lawyerDetails?.firstName || "";
        const middle = item?.middleName || item?.lawyerDetails?.middleName || "";
        const last = item?.lastName || item?.lawyerDetails?.lastName || "";
        const fullName = `${first} ${middle} ${last}`.trim() || item?.lawyerName || item?.name || "";

        const barCouncilId = item?.barCouncilId || item?.barCouncilNumber || item?.barCouncilNo || item?.lawyerDetails?.barCouncilId || "";

        const rawBankList = Array.isArray(item?.bankDetails)
            ? item.bankDetails
            : (Array.isArray(item?.lawyerDetailResponse?.bankDetails)
                ? item.lawyerDetailResponse.bankDetails
                : (item?.bankDetails ? [item.bankDetails] : [item]));
        const bank = rawBankList.find(b => Boolean(b?.primary)) || rawBankList[0] || item?.bankDetails || item || {};
        const accNo = bank?.decryptAccountNumber || bank?.accountNumber || item?.decryptAccountNumber || item?.accountNumber || "";
        const ifscCode = bank?.ifsc || bank?.ifscCode || item?.ifsc || item?.ifscCode || "";

        const splitLabel = item?.lawyerNo || item?.lawyerId || item?.id || item?.requestId || "";

        return [
            index + 1,
            item?.submerchantId || "",
            fullName,
            barCouncilId,
            accNo ? String(accNo).trim() : "",
            ifscCode,
            splitLabel
        ].map(escapeCsvValue).join(",");
    });

    const csvContent = "\uFEFF" + [headers.map(escapeCsvValue).join(","), ...rows].join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    if (emailDetails && (emailDetails?.toEmail || emailDetails?.ccEmail)) {
        const readableName = fileName.replace(/_/g, " ");
        const subject = emailDetails?.subject || `${readableName} - ${dayjs().format("DD-MM-YYYY")}`;
        const body = emailDetails?.body || `Hi Team,\n\nPlease find attached the advocate report (${fileName}.csv).\n\nRegards,\nMLawyer Team`;
        openEmailComposer({
            toEmail: emailDetails?.toEmail,
            ccEmail: emailDetails?.ccEmail,
            subject,
            body
        });
    }
};

export const parseErrorMessage = (rawError, defaultMsg = "Failed to process request") => {
    if (!rawError) return defaultMsg;
    const errorStr = typeof rawError === "string"
        ? rawError
        : (rawError?.response?.data?.error?.message ||
           rawError?.response?.data?.message ||
           rawError?.error?.message ||
           rawError?.message ||
           (typeof rawError?.error === "string" ? rawError?.error : "") ||
           defaultMsg);

    if (typeof errorStr !== "string" || !errorStr.trim()) return defaultMsg;

    const jsonMatch = errorStr.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
        try {
            const parsed = JSON.parse(jsonMatch[0]);
            if (parsed?.reason) return parsed.reason;
            if (parsed?.message) return parsed.message;
            if (parsed?.error?.description) return parsed.error.description;
            if (parsed?.error?.message) return parsed.error.message;
            if (typeof parsed?.error === "string") return parsed.error;
        } catch (_) {}
    }
    return errorStr || defaultMsg;
};



